import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Button, Spinner, Alert, Badge, Form, Table, Row, Col, InputGroup,
} from 'react-bootstrap';
import inboundApi from '../../config/inboundApi';

const ROLE_COLORS = { admin: 'danger', agent: 'primary', contributor: 'secondary' };

export default function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();

  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [nonMembers, setNonMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editDefault, setEditDefault] = useState(false);

  // Search within members and non-members
  const [memberSearch, setMemberSearch] = useState('');
  const [nonMemberSearch, setNonMemberSearch] = useState('');

  const fetchGroup = useCallback(async () => {
    try {
      const res = await inboundApi.get(`/groups/${groupId}`);
      setGroup(res.data);
      setMembers(res.data.members || []);
      setEditName(res.data.name);
      setEditDesc(res.data.description || '');
      setEditDefault(res.data.is_default || false);
    } catch {
      setMessage({ type: 'danger', text: 'Failed to load group' });
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  const fetchNonMembers = useCallback(async () => {
    try {
      const params = nonMemberSearch ? { search: nonMemberSearch } : {};
      const res = await inboundApi.get(`/groups/${groupId}/non-members`, { params });
      setNonMembers(res.data);
    } catch {
      // silent
    }
  }, [groupId, nonMemberSearch]);

  useEffect(() => { fetchGroup(); }, [fetchGroup]);
  useEffect(() => { fetchNonMembers(); }, [fetchNonMembers]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      if (editName !== group.name) updates.name = editName;
      if (editDesc !== (group.description || '')) updates.description = editDesc;
      if (editDefault !== (group.is_default || false)) updates.is_default = editDefault;
      if (Object.keys(updates).length === 0) {
        setSaving(false);
        return;
      }
      await inboundApi.put(`/groups/${groupId}`, updates);
      setMessage({ type: 'success', text: 'Group updated' });
      fetchGroup();
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.detail || 'Failed to update group' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (staffId) => {
    try {
      await inboundApi.post(`/groups/${groupId}/members`, { staff_ids: [staffId] });
      fetchGroup();
      fetchNonMembers();
    } catch {
      setMessage({ type: 'danger', text: 'Failed to add member' });
    }
  };

  const handleRemoveMember = async (staffId) => {
    try {
      await inboundApi.delete(`/groups/${groupId}/members/${staffId}`);
      fetchGroup();
      fetchNonMembers();
    } catch {
      setMessage({ type: 'danger', text: 'Failed to remove member' });
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner /></div>;
  }

  if (!group) {
    return <Alert variant="warning">Group not found</Alert>;
  }

  // Filter members client-side by memberSearch
  const filteredMembers = memberSearch
    ? members.filter((m) =>
        m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
        m.email.toLowerCase().includes(memberSearch.toLowerCase())
      )
    : members;

  return (
    <div>
      <Button
        variant="link"
        className="mb-3 p-0"
        onClick={() => navigate('/domain-admin/groups')}
        style={{ textDecoration: 'none' }}
      >
        &larr; Back to Groups
      </Button>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Row>
        {/* Left panel: group details + members */}
        <Col lg={8}>
          <Card className="mb-4">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600 }}>Group Name</Form.Label>
                <Form.Control
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600 }}>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                />
              </Form.Group>
              <Form.Check
                type="checkbox"
                label="Set as default group"
                checked={editDefault}
                onChange={(e) => setEditDefault(e.target.checked)}
                className="mb-3"
              />
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Card.Body>
          </Card>

          <h6 style={{ fontWeight: 600, marginBottom: 12 }}>
            Members ({members.length})
          </h6>

          <InputGroup className="mb-3" style={{ maxWidth: 300 }}>
            <Form.Control
              placeholder="Search members..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
            />
          </InputGroup>

          <Card>
            <Card.Body className="p-0">
              {filteredMembers.length === 0 ? (
                <div className="text-center py-4 text-muted">No members</div>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th className="text-end">Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((m) => (
                      <tr key={m.id}>
                        <td style={{ fontWeight: 600 }}>{m.name}</td>
                        <td><small className="text-muted">{m.email}</small></td>
                        <td>
                          <Badge bg={ROLE_COLORS[m.role] || 'secondary'}>{m.role}</Badge>
                        </td>
                        <td className="text-end">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveMember(m.id)}
                          >
                            &times;
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right panel: add members */}
        <Col lg={4}>
          <Card>
            <Card.Header style={{ fontWeight: 600 }}>Add Group Members</Card.Header>
            <Card.Body>
              <InputGroup className="mb-3">
                <Form.Control
                  placeholder="Search staff..."
                  value={nonMemberSearch}
                  onChange={(e) => setNonMemberSearch(e.target.value)}
                />
              </InputGroup>

              {nonMembers.length === 0 ? (
                <div className="text-center text-muted py-3">
                  <small>No available staff to add</small>
                </div>
              ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {nonMembers.map((s) => (
                    <div
                      key={s.id}
                      className="d-flex justify-content-between align-items-center py-2"
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{s.name}</div>
                        <small className="text-muted">{s.email}</small>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleAddMember(s.id)}
                      >
                        +
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
